/**
 * The follow-up inbox (`/followups`).
 *
 * The scheduling service stores structured facts — an activity, a reason, a
 * date — and nothing else, because it is not allowed to know a contact's name.
 * The Persian sentence a person reads is composed HERE, where the name arrives
 * through the BFF's join. That is not an accident of layering: storing the
 * rendered sentence would freeze the wording at the moment the watchdog ran, so
 * a later rename would leave the inbox quoting a name that no longer exists.
 *
 * Accepting opens the activity dialog PREFILLED — same contact, follow-up type,
 * next working day — because the whole point of a prompt is that acting on it
 * should be faster than ignoring it.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button, Spinner } from '@meshhub/design-tokens/ui';
import { BellRing, Check, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ActivityDialog } from '../components/calendar/ActivityDialog.web.jsx';
import { LoadingOrEmpty, MutationError } from '../components/FormKit.web.jsx';
import { WEEKEND_INDEX, formatDate, persianWeekday } from '../lib/format.js';
import {
  ACCEPT_SUGGESTION,
  ACTIVITY_TYPES_QUERY,
  DISMISS_SUGGESTION,
  FOLLOW_UPS_QUERY,
} from '../lib/queries.js';
import { S } from '../lib/strings.js';

/**
 * The next working day: tomorrow, unless that is Friday.
 *
 * Friday is the weekend in Iran, and a follow-up proposed for it is one the rep
 * has to move before they can accept it — which is exactly the friction a
 * one-click action exists to remove.
 */
export function nextWorkingDay(from = new Date()) {
  const day = new Date(from);
  day.setDate(day.getDate() + 1);
  day.setHours(9, 0, 0, 0);
  if (persianWeekday(day) === WEEKEND_INDEX) day.setDate(day.getDate() + 1);
  return day;
}

/**
 * The sentence, assembled from a name and a date.
 *
 * Exported so a test can assert on it without rendering a screen — the
 * composition is the interesting part, not the markup around it.
 */
export function composeSentence(suggestion) {
  const name = suggestion.contact?.displayName || S.app.none;
  const subject = suggestion.activity?.title
    ? `${suggestion.activity.title} (${name})`
    : name;
  const date = formatDate(suggestion.activity?.startsAt ?? suggestion.suggestedAt);
  const template = S.followUps.sentence[suggestion.reason];
  return template ? template(subject, date) : subject;
}

export function FollowUpsScreen() {
  const [accepting, setAccepting] = useState(null);
  const { data, loading, error, refetch } = useQuery(FOLLOW_UPS_QUERY, {
    variables: { state: 'OPEN' },
    fetchPolicy: 'cache-and-network',
  });
  const { data: typeData } = useQuery(ACTIVITY_TYPES_QUERY);

  const [acceptSuggestion, acceptState] = useMutation(ACCEPT_SUGGESTION, {
    onCompleted: () => refetch(),
  });
  const [dismissSuggestion, dismissState] = useMutation(DISMISS_SUGGESTION, {
    onCompleted: () => refetch(),
  });

  const suggestions = data?.followUpSuggestions ?? [];

  /**
   * The follow-up type, by CODE rather than position.
   *
   * The one place a code is named — and it names a *mechanism* («this is the
   * kind of activity a follow-up is»), not a Parsina concept. A deployment
   * whose vocabulary has no such code simply gets its first type, which is
   * still usable; the alternative, hardcoding an index, would silently change
   * meaning the first time somebody reordered the list.
   */
  const followUpType = useMemo(() => {
    const types = typeData?.activityTypes ?? [];
    return types.find((type) => type.code === 'SALES_FOLLOW_UP') ?? types[0];
  }, [typeData]);

  function prefillFor(suggestion) {
    const start = nextWorkingDay();
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    return {
      startsAt: start,
      endsAt: end,
      contactId: suggestion.contactId ?? '',
      typeId: followUpType?.id ?? '',
      title: suggestion.activity?.title
        ? `${S.activity.createdFromInteraction}: ${suggestion.activity.title}`
        : S.followUps.accept,
    };
  }

  return (
    <div className="flex h-screen flex-col">
      <header className="flex h-toolbar shrink-0 items-center gap-3 border-b border-border
                         bg-surface px-5">
        <BellRing size={18} className="text-primary" aria-hidden="true" />
        <h1 className="text-md font-bold">{S.followUps.title}</h1>
        {suggestions.length > 0 && (
          <span className="rounded-full bg-primary px-2 py-px text-xs text-primary-contrast">
            {suggestions.length}
          </span>
        )}
      </header>

      <MutationError error={error ?? acceptState.error ?? dismissState.error} />

      <div className="flex-1 overflow-y-auto p-5">
        {loading && suggestions.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <LoadingOrEmpty
            loading={false}
            empty={suggestions.length === 0}
            emptyTitle={S.followUps.empty}
            emptyHint={S.followUps.emptyHint}
          />
        )}

        <ul className="flex flex-col gap-2">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              className="flex items-center gap-4 rounded border border-border bg-surface
                         px-4 py-3"
            >
              <div className="flex flex-1 flex-col gap-1">
                <p className="text-base text-text">{composeSentence(suggestion)}</p>
                <div className="flex items-center gap-3 text-xs text-text-muted">
                  <span className="rounded bg-surface-hover px-2 py-px">
                    {S.followUps.reasonLabels[suggestion.reason] ?? suggestion.reason}
                  </span>
                  {suggestion.contactId && (
                    <Link
                      to={`/contacts/${suggestion.contactId}`}
                      className="text-primary hover:underline"
                    >
                      {suggestion.contact?.displayName ?? S.activity.contact}
                    </Link>
                  )}
                </div>
              </div>

              <Button
                type="button"
                variant="primary"
                onClick={() => setAccepting(suggestion)}
              >
                <Check size={14} aria-hidden="true" />
                {S.followUps.accept}
              </Button>
              <Button
                type="button"
                onClick={() => dismissSuggestion({ variables: { id: suggestion.id } })}
              >
                <X size={14} aria-hidden="true" />
                {S.followUps.dismiss}
              </Button>
            </li>
          ))}
        </ul>
      </div>

      {/*
        Accepting routes through the same dialog as any other activity, prefilled.
        A bespoke "accept" form would be a second place the DONE-requires-result
        and reminder rules had to be kept in step.

        `onCreate` is what makes it an ACCEPT rather than a plain create: the
        input goes to `acceptSuggestion`, which creates the activity AND resolves
        the suggestion in one transaction. Letting the dialog create it as well
        would leave two activities and an inbox row that never cleared.
      */}
      <ActivityDialog
        open={Boolean(accepting)}
        onOpenChange={(open) => !open && setAccepting(null)}
        prefill={accepting ? prefillFor(accepting) : undefined}
        onCreate={async (input) => {
          await acceptSuggestion({ variables: { id: accepting.id, input } });
          setAccepting(null);
        }}
      />
    </div>
  );
}
